import { createContext, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePostLoginFlow } from '../hooks/usePostLoginFlow';

interface EnhancedAuthContextType {
  // Re-export all AuthContext properties
  user: any;
  token: string | null;
  stores: any[];
  currentShop: string | null;
  loading: boolean;
  currentPlan: any;
  currentSubscription: any;
  isFreePlan: boolean;
  billingDaysRemaining: number | null;
  hasFeature: (feature: string) => boolean;
  hasPlanFeature: (featureName: string) => boolean;
  getPlanFeatureNumeric: (featureName: string) => number | null;
  getPlanFeatureText: (featureName: string) => string | null;
  canUsePredictions: () => boolean;
  getPredictionLimit: () => number;
  hasUnlimitedPredictions: () => boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (
    name: string,
    email: string,
    password: string,
    websiteUrl?: string,
    passwordConfirmation?: string,
    phoneNumber?: string
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  loginWithFacebook: () => Promise<{ success: boolean; error?: string }>;
  checkAuth: (force?: boolean) => Promise<void>;
  selectStore: (shop: string | null) => void;
  refreshStores: () => Promise<void>;
  loadSubscriptionData: (shop: string) => Promise<void>;
  updateUser: (userData: Partial<any>) => void;
  sessionExpiresAt?: number | null;

  // Enhanced post-login flow properties
  postLoginLoading: boolean;
  postLoginError: string | null;
  pendingInvitations: any[];
  executePostLoginFlow: (invitations?: any[]) => Promise<any>;
  selectAndNavigateToStore: (store: any) => Promise<void>;
  acceptInvitation: (invitation: any) => Promise<void>;
  loadPendingInvitations: () => Promise<void>;
  shouldShowStoreHub: boolean;
  clearPostLoginError: () => void;
}

const EnhancedAuthContext = createContext<EnhancedAuthContextType | undefined>(undefined);

export function useEnhancedAuth() {
  const context = useContext(EnhancedAuthContext);
  if (context === undefined) {
    throw new Error('useEnhancedAuth must be used within an EnhancedAuthProvider');
  }
  return context;
}

interface EnhancedAuthProviderProps {
  children: ReactNode;
}

export function EnhancedAuthProvider({ children }: EnhancedAuthProviderProps) {
  const authContext = useAuth();
  const navigate = useNavigate();
  const postLoginFlow = usePostLoginFlow(navigate);

  // Combine the contexts
  const enhancedContext: EnhancedAuthContextType = {
    // AuthContext properties
    ...authContext,
    
    // Enhanced post-login flow properties
    postLoginLoading: postLoginFlow.isLoading,
    postLoginError: postLoginFlow.error,
    pendingInvitations: postLoginFlow.pendingInvitations,
    executePostLoginFlow: postLoginFlow.executePostLoginFlow,
    selectAndNavigateToStore: postLoginFlow.selectAndNavigateToStore,
    acceptInvitation: postLoginFlow.acceptInvitation,
    loadPendingInvitations: postLoginFlow.loadPendingInvitations,
    shouldShowStoreHub: postLoginFlow.shouldShowStoreHub,
    clearPostLoginError: postLoginFlow.clearError,
  };

  return (
    <EnhancedAuthContext.Provider value={enhancedContext}>
      {children}
    </EnhancedAuthContext.Provider>
  );
}
